const GIS_SRC = 'https://accounts.google.com/gsi/client'

let loadPromise: Promise<NonNullable<Window['google']>> | null = null

export function loadGis(): Promise<NonNullable<Window['google']>> {
  if (loadPromise) return loadPromise

  loadPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve(window.google)
      return
    }

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GIS_SRC}"]`,
    )
    const script = existing ?? document.createElement('script')
    const cleanup = () => {
      script.removeEventListener('load', onLoad)
      script.removeEventListener('error', onError)
    }
    const onLoad = () => {
      cleanup()
      if (window.google?.accounts?.id) resolve(window.google)
      else reject(new Error('Google Identity Services failed to initialize'))
    }
    const onError = () => {
      cleanup()
      // Drop the failed tag so a retry re-inserts a fresh one instead of
      // listening on a script whose error event already fired.
      script.remove()
      reject(new Error('Failed to load Google Identity Services'))
    }

    script.addEventListener('load', onLoad)
    script.addEventListener('error', onError)

    if (!existing) {
      script.src = GIS_SRC
      script.async = true
      script.defer = true
      document.head.appendChild(script)
    }
  })

  // Don't cache a rejection: the silent-renewal path depends on loadGis(), and
  // a permanently cached failure (one offline blip) would kill every future
  // renewal until a full page reload. Let the next caller retry the load.
  loadPromise.catch(() => {
    loadPromise = null
  })

  return loadPromise
}

// ---------------------------------------------------------------------------
// Single owner of google.accounts.id.initialize().
//
// GIS keeps ONE page-global config: every initialize() call replaces the
// previous callback (GSI even logs a warning), so independent call sites
// (login button, silent renewal) silently steal each other's credentials.
// Instead, initialize exactly once with a dispatcher callback and let flows
// claim/release the live handler.
//
// The shared config works for both flows:
// - ux_mode 'popup' only affects the rendered button (Login), and only when
//   the FedCM button flow is unavailable (see use_fedcm_for_button below).
// - auto_select only affects prompt()-driven One Tap / FedCM auto-reauthn,
//   which only silentReauth uses; Login never calls prompt().
// - use_fedcm_for_button routes button sign-in through the browser's FedCM
//   active-mode dialog (Chromium 125+) instead of the popup window. That
//   establishes the per-origin FedCM connected-account state at login, which
//   is what lets the hourly prompt() auto-reauthn renew silently — without it,
//   the first renewal on a fresh browser profile needs an interactive dialog
//   mid-session. Non-FedCM browsers (Safari, Firefox) fall back to the popup.

type CredentialHandler = (credential: string) => void

let credentialHandler: CredentialHandler | null = null
let idInitialized = false

// Route GIS credentials to `handler` until the returned release function is
// called. Release only clears the slot if this handler still owns it, so a
// later claimant is never torn down by a stale release.
export function claimGoogleCredentialHandler(
  handler: CredentialHandler,
): () => void {
  credentialHandler = handler
  return () => {
    if (credentialHandler === handler) credentialHandler = null
  }
}

export function initGoogleId(): Promise<NonNullable<Window['google']>> {
  return loadGis().then((google) => {
    if (!idInitialized) {
      google.accounts.id.initialize({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        ux_mode: 'popup',
        auto_select: true,
        use_fedcm_for_prompt: true,
        use_fedcm_for_button: true,
        callback: ({ credential }) => credentialHandler?.(credential),
      })
      idInitialized = true
    }
    return google
  })
}
